import React, { useCallback, useEffect } from "react";

import { CrewCognitiveLoad as CrewCognitiveLoadComponent } from "heat-next";

import { useModal } from "@/context/modal/modal";
import { useSessionDetailsContext } from "@/context/session-details/session-details";

import { ComponentWrapper } from "@/pages/shared/shared.styles";

import ContentLoader from "@/components/content-loader/content-loader";
import EmptyState from "@/components/empty-state/empty-state";
import { EmptyStateType } from "@/components/empty-state/empty-state.types";
import CrewModalContent from "@/components/ui/modal-with-context/modal-contents/session-details-page/crew-modal-content/crew-modal-content";

import base from "@/themes/base/base";

import type { CrewFilterOption } from "./crew-cognitive-load.types";

type ScoreData = { name: string };

const CrewCognitiveLoad = () => {
    //TODO: implement data, isFetching & isError from sessionDetailsContext

    const { openModal, setWidth, setHeight } = useModal();

    const {
        isFetchingCrewCognitiveLoad,
        crewCognitiveLoadData,
        isErrorCrewCognitiveLoad,
        crewFilterOptions,
        setCrewFilterOptions,
        setSelectedCrewFilterOption,
        setSelectedCrewIndex,
        subDimensionsData,
        isFetchingSubDimensions,
    } = useSessionDetailsContext();

    const filterOptions: CrewFilterOption[] = useCallback(
        crewCognitiveLoadData?.[0].datasets?.[0].data?.reduce(
            (acc: any[], dataObj: ScoreData, index: number) => {
                const { name } = dataObj;

                const code = name.replace(" ", "").toLowerCase();

                const filtersArray = {
                    name: name,
                    code: code,
                    scenarioName: `Scenario ${index + 1}`,
                };

                acc.push(filtersArray);

                return acc;
            },
            [],
        ),
        [crewCognitiveLoadData],
    );

    useEffect(() => {
        setCrewFilterOptions(filterOptions);
    }, [crewCognitiveLoadData]);

    const handleCrewClickModalOpen = useCallback(() => {
        setWidth("77.25682295%"); //for 1104px modal width, in breakpoint 1440px
        setHeight("87.4%");

        setSelectedCrewFilterOption(crewFilterOptions && crewFilterOptions[0]);
        setSelectedCrewIndex(0);

        openModal({
            content: <CrewModalContent subDimensionsData={subDimensionsData} />,
            backgroundColor: `${base.colors.neutral.grey[900]}`,
            borderRadius: "0.25rem",
            padding: "1.5rem 1.5rem 0 1.5rem",
            boxShadow: "0px 0px 4px 1px rgba(255, 255, 255, 0.25)",
        });
    }, [setWidth, setHeight, openModal, crewFilterOptions, subDimensionsData]);

    if (isFetchingCrewCognitiveLoad) {
        return <ContentLoader height="26.1875rem" />;
    }

    if (isErrorCrewCognitiveLoad && !isFetchingCrewCognitiveLoad) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="error-state:title"
                    description="error-state:description"
                    emptyStateType={EmptyStateType.Error}
                    minHeight="23.1875rem"
                />
            </ComponentWrapper>
        );
    }

    if (
        !isErrorCrewCognitiveLoad &&
        (crewCognitiveLoadData === null || crewCognitiveLoadData?.dataset?.length === 0)
    ) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="session-details:crew-cognitive-load:empty-state:title"
                    emptyStateType={EmptyStateType.Charts}
                    minHeight="23.1875rem"
                />
            </ComponentWrapper>
        );
    }

    return (
        <CrewCognitiveLoadComponent
            data={crewCognitiveLoadData}
            onClick={handleCrewClickModalOpen}
            isDataLoading={isFetchingSubDimensions}
        />
    );
};

export default CrewCognitiveLoad;
